import React, { useState } from 'react';
import { BarChart, PieChart, ChevronDown, Download, Home, BarChart2 } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент для визуализации теплового баланса здания
 */
const HeatBalanceVisualization = ({ data, results, showFullResults, setShowFullResults }) => {
  const [activeTab, setActiveTab] = useState('chart');
  
  if (!data || !results) {
    return null;
  }

  // Задаем цвета для различных элементов теплового баланса
  const colors = {
    walls: '#ef4444', // red-500
    windows: '#f97316', // orange-500
    roof: '#8b5cf6', // violet-500
    floor: '#0ea5e9', // sky-500
    ventilation: '#84cc16' // lime-500
  };
  
  // Форматируем данные для диаграммы
  const chartData = data.labels.map((label, index) => ({
    name: label,
    value: data.data[index],
    percentage: results.heatLossPercentages[Object.keys(results.heatLossPercentages)[index]],
    color: Object.values(colors)[index]
  }));
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart2 className="w-5 h-5 mr-2 text-red-500" />
          Тепловой баланс здания
        </h3>

        {/* Кнопка переключения полных/кратких результатов */}
        <Button
          size="sm"
          onClick={() => setShowFullResults(prev => !prev)}
          variant="outline"
          aria-expanded={showFullResults}
        >
          {showFullResults ? 'Показать краткие результаты' : 'Показать подробный анализ'}
          <ChevronDown className={`w-4 h-4 ml-1 transition-transform ${showFullResults ? 'rotate-180' : ''}`} />
        </Button>
      </div>

      {/* Вкладки для разных типов отображения */}
      <div className="flex space-x-1 border-b border-gray-200 dark:border-gray-700 mb-4">
        <button
          onClick={() => setActiveTab('chart')}
          className={`py-2 px-4 text-sm font-medium rounded-t-lg ${
            activeTab === 'chart'
              ? 'text-red-600 dark:text-red-400 border-b-2 border-red-500 dark:border-red-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          <div className="flex items-center gap-1.5">
            <PieChart className="w-4 h-4" />
            Круговая диаграмма
          </div>
        </button>
        <button
          onClick={() => setActiveTab('bar')}
          className={`py-2 px-4 text-sm font-medium rounded-t-lg ${
            activeTab === 'bar'
              ? 'text-red-600 dark:text-red-400 border-b-2 border-red-500 dark:border-red-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          <div className="flex items-center gap-1.5">
            <BarChart className="w-4 h-4" />
            Столбчатая диаграмма
          </div>
        </button>
        <button
          onClick={() => setActiveTab('table')}
          className={`py-2 px-4 text-sm font-medium rounded-t-lg ${
            activeTab === 'table'
              ? 'text-red-600 dark:text-red-400 border-b-2 border-red-500 dark:border-red-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          <div className="flex items-center gap-1.5">
            <Home className="w-4 h-4" />
            Теплопотери по элементам
          </div>
        </button>
      </div>

      {/* Контейнер диаграммы */}
      <div className="p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
        {activeTab === 'chart' && (
          <div className="flex flex-col items-center">
            <div className="relative w-full max-w-md h-64 mx-auto">
              <PieChartVisualization data={chartData} />
            </div>
            <div className="mt-4 w-full">
              <div className="flex flex-wrap justify-center gap-4">
                {chartData.map((item, index) => (
                  <div key={index} className="flex items-center">
                    <div 
                      className="w-3 h-3 rounded-full mr-2" 
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{item.name}: {formatNumber(item.percentage, 1)}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'bar' && (
          <div className="h-80 w-full">
            <BarChartVisualization data={chartData} />
          </div>
        )}

        {activeTab === 'table' && (
          <div className="overflow-x-auto heat-chart-table rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="w-full text-sm text-left text-gray-700 dark:text-gray-300">
              <thead className="text-xs text-gray-700 dark:text-gray-300 uppercase bg-gray-100 dark:bg-gray-700">
                <tr>
                  <th scope="col" className="px-4 py-3 font-semibold">Элемент здания</th>
                  <th scope="col" className="px-4 py-3 font-semibold text-right">Теплопотери (Вт)</th>
                  <th scope="col" className="px-4 py-3 font-semibold text-right">Доля (%)</th>
                  <th scope="col" className="px-4 py-3 font-semibold text-right">U-value (Вт/м²·°C)</th>
                  {showFullResults && (
                    <>
                      <th scope="col" className="px-4 py-3 font-semibold text-right">Площадь (м²)</th>
                      <th scope="col" className="px-4 py-3 font-semibold text-right">ΔT (°C)</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-4 py-3 font-medium">Стены</td>
                  <td className="px-4 py-3 text-right">{formatNumber(results.heatLosses.walls, 0)}</td>
                  <td className="px-4 py-3 text-right">{formatNumber(results.heatLossPercentages.walls, 1)}%</td>
                  <td className="px-4 py-3 text-right">{formatNumber(results.uValues.walls, 3)}</td>
                  {showFullResults && (
                    <>
                      <td className="px-4 py-3 text-right">{formatNumber(results.areas?.walls || 0, 1)}</td>
                      <td className="px-4 py-3 text-right">{formatNumber(results.temperatureDifference, 1)}</td>
                    </>
                  )}
                </tr>
                <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-4 py-3 font-medium">Окна</td>
                  <td className="px-4 py-3 text-right">{formatNumber(results.heatLosses.windows, 0)}</td>
                  <td className="px-4 py-3 text-right">{formatNumber(results.heatLossPercentages.windows, 1)}%</td>
                  <td className="px-4 py-3 text-right">{formatNumber(results.uValues.windows, 3)}</td>
                  {showFullResults && (
                    <>
                      <td className="px-4 py-3 text-right">{formatNumber(results.areas?.windows || 0, 1)}</td>
                      <td className="px-4 py-3 text-right">{formatNumber(results.temperatureDifference, 1)}</td>
                    </>
                  )}
                </tr>
                <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-4 py-3 font-medium">{results.roofDescription}</td>
                  <td className="px-4 py-3 text-right">{formatNumber(results.heatLosses.roof, 0)}</td>
                  <td className="px-4 py-3 text-right">{formatNumber(results.heatLossPercentages.roof, 1)}%</td>
                  <td className="px-4 py-3 text-right">{formatNumber(results.uValues.roof, 3)}</td>
                  {showFullResults && (
                    <>
                      <td className="px-4 py-3 text-right">{formatNumber(results.areas?.roof || 0, 1)}</td>
                      <td className="px-4 py-3 text-right">{formatNumber(results.temperatureDifference, 1)}</td>
                    </>
                  )}
                </tr>
                <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-4 py-3 font-medium">{results.floorDescription}</td>
                  <td className="px-4 py-3 text-right">{formatNumber(results.heatLosses.floor, 0)}</td>
                  <td className="px-4 py-3 text-right">{formatNumber(results.heatLossPercentages.floor, 1)}%</td>
                  <td className="px-4 py-3 text-right">{formatNumber(results.uValues.floor, 3)}</td>
                  {showFullResults && (
                    <>
                      <td className="px-4 py-3 text-right">{formatNumber(results.areas?.floor || 0, 1)}</td>
                      <td className="px-4 py-3 text-right">{formatNumber(results.temperatureDifference, 1)}</td>
                    </>
                  )}
                </tr>
                <tr className="bg-gray-50 dark:bg-gray-700/50">
                  <td className="px-4 py-3 font-medium">Вентиляция и инфильтрация</td>
                  <td className="px-4 py-3 text-right">{formatNumber(results.heatLosses.ventilation, 0)}</td>
                  <td className="px-4 py-3 text-right">{formatNumber(results.heatLossPercentages.ventilation, 1)}%</td>
                  <td className="px-4 py-3 text-right">{formatNumber(results.airChangeRate || 0, 2)} ч⁻¹</td>
                  {showFullResults && (
                    <>
                      <td className="px-4 py-3 text-right">{formatNumber(results.volume || 0, 1)} м³</td>
                      <td className="px-4 py-3 text-right">{formatNumber(results.temperatureDifference, 1)}</td>
                    </>
                  )}
                </tr>
                <tr className="bg-red-50 dark:bg-red-900/20 font-semibold">
                  <td className="px-4 py-3">ВСЕГО</td>
                  <td className="px-4 py-3 text-right">{formatNumber(results.totalHeatLoss, 0)}</td>
                  <td className="px-4 py-3 text-right">100%</td>
                  <td className="px-4 py-3 text-right">{formatNumber(results.overallUValue || 0, 3)}</td>
                  {showFullResults && (
                    <>
                      <td className="px-4 py-3 text-right">-</td>
                      <td className="px-4 py-3 text-right">{formatNumber(results.temperatureDifference, 1)}</td>
                    </>
                  )}
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showFullResults && (
        <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Дополнительная информация</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Объем помещения:</span>
              <span className="font-medium text-gray-800 dark:text-gray-200">{formatNumber(results.volume, 1)} м³</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Площадь наружных стен:</span>
              <span className="font-medium text-gray-800 dark:text-gray-200">{formatNumber(results.areas?.walls || 0, 1)} м²</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Доля окон от площади стен:</span>
              <span className="font-medium text-gray-800 dark:text-gray-200">{formatNumber(results.windowWallRatio * 100 || 0, 1)}%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Площадь {results.roofDescription.toLowerCase()}:</span>
              <span className="font-medium text-gray-800 dark:text-gray-200">{formatNumber(results.areas?.roof || 0, 1)} м²</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Кратность воздухообмена:</span>
              <span className="font-medium text-gray-800 dark:text-gray-200">{formatNumber(results.airChangeRate || 0, 2)} ч⁻¹</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Среднегодовая температура:</span>
              <span className="font-medium text-gray-800 dark:text-gray-200">{formatNumber(results.avgAnnualTemperature || 0, 1)}°C</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Компонент для отображения круговой диаграммы
const PieChartVisualization = ({ data }) => {
  // Простая реализация круговой диаграммы с использованием SVG
  const total = data.reduce((sum, item) => sum + item.value, 0);
  let startAngle = 0;
  
  return (
    <svg viewBox="0 0 100 100" className="w-full h-full">
      <g transform="translate(50, 50)">
        {data.map((item, index) => {
          const percentage = (item.value / total) * 100;
          const angle = (percentage / 100) * 360;
          const endAngle = startAngle + angle;
          
          // Преобразование углов в радианы для расчета координат
          const startRad = (startAngle * Math.PI) / 180;
          const endRad = (endAngle * Math.PI) / 180;
          
          // Координаты для дуги
          const x1 = 40 * Math.sin(startRad);
          const y1 = -40 * Math.cos(startRad);
          const x2 = 40 * Math.sin(endRad);
          const y2 = -40 * Math.cos(endRad);
          
          // Флаг для определения большой дуги
          const largeArcFlag = angle > 180 ? 1 : 0;
          
          // Формирование пути для сектора диаграммы
          const pathData = `M 0 0 L ${x1} ${y1} A 40 40 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;
          
          // Текст для сектора
          const textRad = startRad + (endRad - startRad) / 2;
          const textX = 25 * Math.sin(textRad);
          const textY = -25 * Math.cos(textRad);
          
          // Сохраняем угол для следующего сектора
          startAngle = endAngle;
          
          return (
            <g key={index}>
              <path
                d={pathData}
                fill={item.color}
                stroke="#fff"
                strokeWidth="0.5"
              />
              {percentage > 5 && (
                <text
                  x={textX}
                  y={textY}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fill="#fff"
                  fontSize="4"
                  fontWeight="bold"
                >
                  {Math.round(percentage)}%
                </text>
              )}
            </g>
          );
        })}
      </g>
    </svg>
  );
};

// Компонент для отображения столбчатой диаграммы
const BarChartVisualization = ({ data }) => {
  const maxValue = Math.max(...data.map(item => item.value));
  const barWidth = 100 / (data.length * 2); // Ширина столбца с учетом промежутков
  
  return (
    <svg viewBox="0 0 100 100" className="w-full h-full" preserveAspectRatio="none">
      {/* Горизонтальные линии сетки */}
      {[0, 25, 50, 75, 100].map((line, index) => (
        <g key={index}>
          <line
            x1="0"
            y1={100 - line}
            x2="100"
            y2={100 - line}
            stroke="#e5e7eb"
            strokeWidth="0.5"
          />
          <text
            x="-1"
            y={100 - line}
            fontSize="3"
            fill="#6b7280"
            textAnchor="end"
            dominantBaseline="middle"
          >
            {Math.round((line / 100) * maxValue)}
          </text>
        </g>
      ))}
      
      {/* Столбцы */}
      {data.map((item, index) => {
        const barHeight = (item.value / maxValue) * 100;
        const x = index * (barWidth * 2) + barWidth / 2;
        
        return (
          <g key={index}>
            <rect
              x={x}
              y={100 - barHeight}
              width={barWidth}
              height={barHeight}
              fill={item.color}
              rx="1"
              ry="1"
            />
            <text
              x={x + barWidth / 2}
              y="103"
              fontSize="3"
              textAnchor="middle"
              fill="#4b5563"
            >
              {item.name.split('/')[0]}
            </text>
            <text
              x={x + barWidth / 2}
              y={95 - barHeight}
              fontSize="3"
              fontWeight="bold"
              textAnchor="middle"
              fill="#4b5563"
            >
              {Math.round(item.percentage)}%
            </text>
          </g>
        );
      })}
    </svg>
  );
};

export default HeatBalanceVisualization;